import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  params,
  shape,
  fragment,
  string as stringType,
  useDataModel,
  customType,
  arrayOf
} from '@thd-nucleus/data-sources';
import classNames from 'classnames';
import { HotspotDrawerContainer } from './hotspot-drawer/HotspotDrawerContainer';
import { HotspotBlock } from './HotspotBlock';
import { VideoPlayer } from './VideoPlayer';

const { string, number } = PropTypes;

const renderResponsiveImage = (ssrImageData, title = '', enableB2CRadius = false, lazyLoadImages = false) => {
  const imageRadiusClass = classNames('sui-w-full', { 'sui-rounded-[12px]': enableB2CRadius });

  return (
    <picture className={imageRadiusClass}>
      {ssrImageData?.responsiveImages?.map(({ imageSrc, mediaQuery }, index) => (
        <source
          key={index}
          className={imageRadiusClass}
          alt={title}
          srcSet={imageSrc}
          media={mediaQuery}
        />
      ))}
      {/* eslint-disable-next-line @mizdra/layout-shift/require-size-attributes */}
      <img
        className={imageRadiusClass}
        alt={title}
        src={ssrImageData?.mobileImageSrc}
        loading={lazyLoadImages ? 'lazy' : 'eager'}
        // eslint-disable-next-line react/no-unknown-property
        fetchPriority={lazyLoadImages ? 'low' : 'high'}
      />
    </picture>
  );
};

const ImageHotspot = ({
  componentId, componentClass, position, ssrImageData, enableB2CRadius = false,
  lazyLoadImages = false, enableSSR = false
}) => {
  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('image-hotspot.ready');
  }, []);

  const { data, error } = useDataModel('component', {
    variables: {
      id: componentId,
      componentClass,
    },
    ssr: enableSSR,
  });

  const componentData = data?.component;

  if (error && !componentData && !ssrImageData) {
    return null;
  }

  const finalDesktopUrl = ssrImageData?.responsiveImages?.[0]?.imageSrc;
  const finalMobileUrl = ssrImageData?.mobileImageSrc;
  const mobilePosterImageUrl = ssrImageData?.mobilePosterImageUrl || null;

  const desktopVideoUrl = finalDesktopUrl && finalDesktopUrl.toLowerCase().endsWith('.mp4') ? finalDesktopUrl : null;

  const mobileVideoUrl = finalMobileUrl && finalMobileUrl.toLowerCase().endsWith('.mp4') ? finalMobileUrl : null;

  const hasVideo = !!(desktopVideoUrl || mobileVideoUrl);

  const title = componentData?.title || '';
  const hotspots = componentData?.hotspots ? JSON.parse(componentData.hotspots) : null;
  const hotspotArr = hotspots?.hotspots ? hotspots.hotspots.reverse() : null;
  const actions = componentData?.hotspotActions;
  const videoClasses = classNames('sui-w-full sui-h-full sui-object-cover', { 'sui-rounded-[12px]': enableB2CRadius });

  const fetchPriority = lazyLoadImages ? 'low' : 'high';
  const imageLoadingPriority = lazyLoadImages ? 'lazy' : 'eager';

  return (
    <div
      className="sui-relative sui-mx-auto sui-my-0 sui-w-full sui-h-full"
      data-component="ImageHotspot"
    >
      {hasVideo ? (
        <VideoPlayer
          desktopVideoUrl={desktopVideoUrl}
          mobileVideoUrl={mobileVideoUrl}
          altText={title}
          videoClasses={videoClasses}
          mobilePosterImageUrl={mobilePosterImageUrl}
        />
      ) : (
        <>
          {!ssrImageData ? (
            /* eslint-disable-next-line @mizdra/layout-shift/require-size-attributes */
            <img
              className={classNames('sui-w-full', { 'sui-rounded-[12px]': enableB2CRadius })}
              src={componentData?.imageUrl}
              alt={title}
              loading={imageLoadingPriority}
              // eslint-disable-next-line react/no-unknown-property
              fetchPriority={fetchPriority}
            />
          ) : (
            renderResponsiveImage(ssrImageData, title, enableB2CRadius, lazyLoadImages)
          )}
        </>
      )}

      {hotspotArr
        && hotspotArr.map((hotspot, idx) => {
          const hotspotStyle = {
            width: `${hotspot.width}%`,
            height: `${hotspot.height}%`,
            left: `${hotspot.x}%`,
            top: `${hotspot.y}%`,
            zIndex: 10,
          };
          const action = actions?.filter((actionItem) => actionItem.title === hotspot.name);

          if (action?.[0]?.drawer?.headline) {
            return (
              <HotspotDrawerContainer
                componentId={componentId}
                key={`hotspot-${idx}-${action[0].id}`}
                style={hotspotStyle}
                headline={action[0]?.drawer?.headline}
              />
            );
          }

          if (action?.[0]?.id && action[0].url) {
            return (
              <HotspotBlock
                key={`hotspot-${idx}-${action[0].id}`}
                id={action[0].id}
                position={position}
                style={hotspotStyle}
                action={action[0].url}
                name={hotspot.name}
              />
            );
          }
          return null;
        })}
    </div>
  );
};

ImageHotspot.propTypes = {
  componentId: string.isRequired,
  componentClass: string,
  position: PropTypes.oneOfType([string, number]),
  ssrImageData: PropTypes.shape({
    mobileImageSrc: string,
    mobilePosterImageUrl: string,
    responsiveImages: PropTypes.arrayOf(
      PropTypes.shape({
        imageSrc: string,
        mediaQuery: string,
      })
    ),
  }),
  enableB2CRadius: PropTypes.bool,
  lazyLoadImages: PropTypes.bool,
  enableSSR: PropTypes.bool,
};

ImageHotspot.defaultProps = {
  position: 1,
  ssrImageData: null,
  componentClass: 'ImageHotspot',
  enableB2CRadius: false,
  lazyLoadImages: false,
  enableSSR: false,
};

ImageHotspot.displayName = 'ImageHotspot';

ImageHotspot.dataModel = {
  component: params({
    componentClass: customType('ComponentClass').enum(['ImageHotspot']).isRequired(),
    id: stringType().isRequired(),
  }).shape({
    ImageHotspot: fragment().shape({
      id: stringType(),
      title: stringType(),
      imageUrl: stringType(),
      hotspots: stringType(),
      hotspotActions: arrayOf(shape({
        id: stringType(),
        url: stringType(),
        title: stringType(),
        drawer: shape({
          headline: stringType(),
        }),
      }))
    })
  })
};

export { ImageHotspot, HotspotBlock };
